import type { Api } from '../core/api';
import { log } from '../core/log';

/**
 * Shopify adapter — reads the logged-in customer's id and email from the
 * conventional global slots Shopify themes expose. Auto-detects via the
 * presence of `window.Shopify` (set by every Shopify storefront) and runs
 * a one-shot read at init.
 *
 * Slot priority (first match wins):
 *   1. window.Shopify.customer.email + .id
 *   2. window.__st.cid + window.__st.email   (Shopify analytics global)
 *   3. window.meta.page.customerId           (some themes)
 */

export function detectShopify(): boolean {
	if (typeof window === 'undefined') return false;
	if ((window as any).Shopify) return true;
	if ((window as any).__st && (window as any).__st.cid) return true;
	return false;
}

function readShopifyIdentity(): { id?: string; email?: string } {
	const w = window as any;
	const out: { id?: string; email?: string } = {};

	const fromShopifyCustomer = w.Shopify && w.Shopify.customer;
	if (fromShopifyCustomer) {
		if (fromShopifyCustomer.id) out.id = String(fromShopifyCustomer.id);
		if (typeof fromShopifyCustomer.email === 'string') out.email = fromShopifyCustomer.email;
	}

	const fromSt = w.__st;
	if (fromSt) {
		if (!out.id && fromSt.cid) out.id = String(fromSt.cid);
		if (!out.email && typeof fromSt.email === 'string') out.email = fromSt.email;
	}

	const fromMeta = w.meta && w.meta.page;
	if (fromMeta && !out.id && fromMeta.customerId) out.id = String(fromMeta.customerId);

	return out;
}

export function installShopify(emblue: Api): void {
	if (!detectShopify()) return;
	log('shopify: adapter installed');
	const fire = () => {
		const ident = readShopifyIdentity();
		if (ident.id) emblue.identify(ident.id, ident.email ? { email: ident.email } : undefined, 'shopify');
		else if (ident.email) emblue.setEmail(ident.email, 'shopify');
	};
	// Shopify themes sometimes set these globals after DOM ready (when the
	// theme JS runs). We try once now and once on load.
	fire();
	if (typeof document !== 'undefined' && document.readyState !== 'complete') {
		window.addEventListener('load', fire, { once: true });
	}
}
